import React from "react";
import { Modal } from "../../../../../../../components/core/Modal/Modal";
import {
  CANCEL_LABEL,
  CONFIRM_LABEL,
  COURSE_LABEL,
  UPLOAD_COURSE_CERTIFICATE_LABEL,
} from "../../../../../../../constants/Dictionary";
import {
  Button,
  ButtonVariants,
} from "../../../../../../../components/core/Button/Button";
import { ReactComponent as CheckIcon } from "../../../../../../../icons/check.svg";
import { ReactComponent as CancelIcon } from "../../../../../../../icons/close.svg";
import { FileUploaderSizeEnum } from "../../../../../../../types/AppTypes";
import { FileUploader } from "../../../../../../../components/core/UploadFile/FileUploader";
import {
  CollaboratorCmSheet,
  CollaboratorCmSheetCourse,
} from "../../../../../../../types/api/collaborators.type";
import { EditModalContent } from "../../../SchedaCM.styled";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import * as yup from "yup";
import { FormField } from "../../../../../../../components/core/FormField/FormField";
import { TextBox } from "../../../../../../../components/TextBoxGrid/TextBoxGrid.component";
import { Loader } from "../../../../../../../components/core/Loader/Loader";
import { useUpdateCmSheetCourseCertificate } from "../../../../../../../hooks/cmSheet/courseCertificate.hook";

type FormData = {
  certificate: {
    file?: File | null;
    name?: string | null;
  } | null;
};

interface EditCollaboratorCourseCertificateModalProps {
  course: CollaboratorCmSheetCourse;
  isOpen: boolean;
  closeModal: () => void;
  cmSheetInfo: CollaboratorCmSheet;
}

export const EditCollaboratorCourseCertificateModal = ({
  course,
  isOpen,
  closeModal,
  cmSheetInfo,
}: EditCollaboratorCourseCertificateModalProps) => {
  const schema = yup.object({
    certificate: yup
      .object({
        file: yup.mixed<File>().nullable(),
        name: yup.string().nullable(),
      })
      .nullable(),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const { updateCourseCertificate, isUpdating: loading } =
    useUpdateCmSheetCourseCertificate(cmSheetInfo.id, course.id);

  const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
    try {
      if (dirtyFields.certificate) {
        if (data.certificate && data.certificate.file) {
          await updateCourseCertificate({ file: data.certificate.file });
        }
      }
    } finally {
      closeModal();
    }
  };

  return (
    <Modal
      label={UPLOAD_COURSE_CERTIFICATE_LABEL}
      fullSize={false}
      isOpen={isOpen}
      rightButton={
        <Button
          icon={<CheckIcon />}
          label={CONFIRM_LABEL}
          form={"updateCourse"}
          disabled={!isDirty || loading}
        />
      }
      leftButton={
        <Button
          variant={ButtonVariants.SECONDARY}
          icon={<CancelIcon />}
          label={CANCEL_LABEL}
          onClick={() => closeModal()}
        />
      }
    >
      <Loader loading={loading}>
        <EditModalContent>
          <TextBox label={COURSE_LABEL} text={course.course.name} />
          <form id="updateCourse" onSubmit={handleSubmit(onSubmit)}>
            <FormField error={errors.certificate?.message}>
              <div className="file-uploader-container">
                <Controller
                  name="certificate"
                  control={control}
                  render={({ field }) => {
                    return (
                      <FileUploader
                        size={FileUploaderSizeEnum.REGULAR}
                        onFileUpload={(file: File) => {
                          field.onChange({ file: file, name: file.name });
                        }}
                        value={field.value?.file || null}
                        fileName={field.value?.name! ?? ""}
                        fileSize={
                          field.value?.file ? field.value.file.size : undefined
                        }
                        handleDeleteFile={() => {
                          reset({ certificate: null });
                        }}
                      />
                    );
                  }}
                />
              </div>
            </FormField>
          </form>
        </EditModalContent>
      </Loader>
    </Modal>
  );
};
